import { useState, useEffect } from 'react';
import axios from 'axios';
import styles from './StoreConfigModal.module.css';
import { API_BASE_URL } from '../Config';

const getUserData = () => {
  const user = sessionStorage.getItem('user');
  const token = sessionStorage.getItem('token');

  return {
    user: user ? JSON.parse(user) : null,
    token: token || null
  };
};

const StoreConfigModal = ({ isOpen, onClose }) => {
  const { user } = getUserData();
  const storeId = user?.storeId;

  const [config, setConfig] = useState({
    storeId: '',
    gst: '',
    printType: 'NORMAL',
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    addressLine4: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen && storeId) {
      fetchStoreConfig();
    }
  }, [isOpen, storeId]);

  const fetchStoreConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/store/storeConfig`, {
        params: { storeId }
      });
      if (response.data.status === 'success') {
        setConfig(response.data.payload);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch store configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setConfig(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePrintTypeChange = (e) => {
    setConfig(prev => ({
      ...prev,
      printType: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await axios.post(`${API_BASE_URL}/store/update/storeConfig`, config);
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update store configuration');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Modern Header with Icon */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.iconWrapper}>
              <svg className={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
            </div>
            <div>
              <h2 className={styles.title}>Store Configuration</h2>
              <p className={styles.subtitle}>Manage your store settings and preferences</p>
            </div>
          </div>
          <button 
            className={styles.closeBtn} 
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {loading ? (
          <div className={styles.loaderContainer}>
            <div className={styles.loader}></div>
            <p className={styles.loaderText}>Loading configuration...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.form}>
            {/* Store Details Section */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <path d="M9 3v18"/>
                </svg>
                Store Details
              </h3>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label htmlFor="storeId" className={styles.label}>
                    Store ID
                    <span className={styles.badge}>Read-only</span>
                  </label>
                  <input
                    type="text"
                    id="storeId"
                    name="storeId"
                    value={config.storeId}
                    className={`${styles.input} ${styles.inputDisabled}`}
                    disabled
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="gst" className={styles.label}>
                    GST Number
                    <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    id="gst"
                    name="gst"
                    value={config.gst}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="e.g., 22AAAAA0000A1Z5"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Print Configuration Section */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 9V2h12v7"/>
                  <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/>
                  <rect x="6" y="14" width="12" height="8"/>
                </svg>
                Print Configuration
              </h3>
              <div className={styles.formGroup}>
                <label className={styles.label}>Print Type</label>
                <div className={styles.radioGroup}>
                  <label className={`${styles.radioCard} ${config.printType === 'NORMAL' ? styles.radioCardActive : ''}`}>
                    <input
                      type="radio"
                      name="printType"
                      value="NORMAL"
                      checked={config.printType === 'NORMAL'}
                      onChange={handlePrintTypeChange}
                      className={styles.radioInput}
                    />
                    <div className={styles.radioContent}>
                      <svg className={styles.radioIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2"/>
                        <path d="M8 7h8M8 11h8M8 15h5"/>
                      </svg>
                      <div>
                        <div className={styles.radioTitle}>Normal Print</div>
                        <div className={styles.radioDesc}>Standard A4/Letter size</div>
                      </div>
                    </div>
                  </label>
                  <label className={`${styles.radioCard} ${config.printType === 'THERMAL' ? styles.radioCardActive : ''}`}>
                    <input
                      type="radio"
                      name="printType"
                      value="THERMAL"
                      checked={config.printType === 'THERMAL'}
                      onChange={handlePrintTypeChange}
                      className={styles.radioInput}
                    />
                    <div className={styles.radioContent}>
                      <svg className={styles.radioIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 9V2h12v7"/>
                        <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/>
                        <rect x="6" y="14" width="12" height="8"/>
                      </svg>
                      <div>
                        <div className={styles.radioTitle}>Thermal Print</div>
                        <div className={styles.radioDesc}>Receipt/POS printer</div>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                Store Address
              </h3>
              
              <div className={styles.formGroup}>
                <label htmlFor="addressLine1" className={styles.label}>
                  Address Line 1
                </label>
                <input
                  type="text"
                  id="addressLine1"
                  name="addressLine1"
                  value={config.addressLine1}
                  onChange={handleInputChange}
                  className={styles.input}
                  placeholder="Building number, street name"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="addressLine2" className={styles.label}>
                  Address Line 2
                </label>
                <input
                  type="text"
                  id="addressLine2"
                  name="addressLine2"
                  value={config.addressLine2}
                  onChange={handleInputChange}
                  className={styles.input}
                  placeholder="Landmark, area"
                />
              </div>

              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label htmlFor="addressLine3" className={styles.label}>
                    City
                  </label>
                  <input
                    type="text"
                    id="addressLine3"
                    name="addressLine3"
                    value={config.addressLine3}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="Enter city"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="addressLine4" className={styles.label}>
                    State & PIN Code
                  </label>
                  <input
                    type="text"
                    id="addressLine4"
                    name="addressLine4"
                    value={config.addressLine4}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="State, PIN code"
                  />
                </div>
              </div>
            </div>

            {/* Alert Messages */}
            {error && (
              <div className={styles.alert} role="alert">
                <svg className={styles.alertIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 8v4m0 4h.01"/>
                </svg>
                <div className={styles.alertContent}>
                  <div className={styles.alertTitle}>Error</div>
                  <div className={styles.alertMessage}>{error}</div>
                </div>
              </div>
            )}

            {success && (
              <div className={`${styles.alert} ${styles.alertSuccess}`} role="alert">
                <svg className={styles.alertIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
                  <path d="M22 4L12 14.01l-3-3"/>
                </svg>
                <div className={styles.alertContent}>
                  <div className={styles.alertTitle}>Success</div>
                  <div className={styles.alertMessage}>Configuration updated successfully!</div>
                </div>
              </div>
            )}

            {/* Footer Actions */}
            <div className={styles.footer}>
              <button
                type="button"
                onClick={onClose}
                className={styles.cancelBtn}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.submitBtn}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <svg className={styles.spinner} viewBox="0 0 24 24">
                      <circle className={styles.spinnerCircle} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg className={styles.btnIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
                      <path d="M17 21v-8H7v8M7 3v5h8"/>
                    </svg>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default StoreConfigModal;